"use client";
import React from "react";
import LegalPageLayout from "./../layout/LegalPageLayout";

const TermsOfService = () => {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="November 15, 2023">
      <section>
        <h2>Introduction</h2>
        <p>
          Welcome to our platform. These Terms of Service ("Terms") govern your
          access to and use of our website, services, and applications
          (collectively, the "Service"). By accessing or using the Service, you
          agree to be bound by these Terms. If you disagree with any part of the
          Terms, you may not access the Service.
        </p>
      </section>

      <section>
        <h2>Accounts and Registration</h2>
        <p>
          When you create an account with us, you must provide accurate,
          complete, and current information. Failure to do so constitutes a
          breach of the Terms, which may result in immediate termination of your
          account.
        </p>
        <p>
          You are responsible for safeguarding the password that you use to
          access the Service and for any activities or actions under your
          password. We encourage you to use a "strong" password (a password that
          uses a combination of upper and lower case letters, numbers, and
          symbols) with your account.
        </p>
        <p>
          You agree not to disclose your password to any third party. You must
          notify us immediately upon becoming aware of any breach of security or
          unauthorized use of your account.
        </p>
      </section>

      <section>
        <h2>Google Account Integration</h2>
        <p>
          Our Service allows you to sign in using your Google account. By doing
          so, you authorize us to access certain Google account information,
          such as your name, email address, and profile picture. This
          information is used in accordance with our Privacy Policy.
        </p>
        <p>
          You can revoke our access to your Google account at any time by
          updating your Google Account settings. However, this may limit your
          ability to use certain features of our Service.
        </p>
      </section>

      <section>
        <h2>Content and Conduct</h2>
        <p>
          Our Service allows you to post, link, store, share, and otherwise make
          available certain information, text, graphics, videos, or other
          material ("Content"). You are responsible for the Content that you
          post on or through the Service, including its legality, reliability,
          and appropriateness.
        </p>
        <p>
          By posting Content on or through the Service, you represent and
          warrant that:
        </p>
        <ul>
          <li>
            The Content is yours (you own it) or you have the right to use it
            and grant us the rights and license as provided in these Terms.
          </li>
          <li>
            The posting of your Content on or through the Service does not
            violate the privacy rights, publicity rights, copyrights, contract
            rights, or any other rights of any person.
          </li>
        </ul>
        <p>
          We reserve the right to remove any Content from the Service at our
          discretion, without prior notice, for any reason, including if we
          believe that such Content violates these Terms.
        </p>
      </section>

      <section>
        <h2>Acceptable Use</h2>
        <p>You agree not to use the Service:</p>
        <ul>
          <li>
            In any way that violates any applicable national or international
            law or regulation.
          </li>
          <li>
            To transmit, or procure the sending of, any advertising or
            promotional material, including any "junk mail", "chain letter",
            "spam", or any other similar solicitation.
          </li>
          <li>
            To impersonate or attempt to impersonate the Company, a Company
            employee, another user, or any other person or entity.
          </li>
          <li>
            In any way that infringes upon the rights of others, or in any way
            is illegal, threatening, fraudulent, or harmful.
          </li>
          <li>
            To engage in any other conduct that restricts or inhibits anyone's
            use or enjoyment of the Service, or which, as determined by us, may
            harm the Company or users of the Service or expose them to
            liability.
          </li>
        </ul>
      </section>

      <section>
        <h2>Intellectual Property</h2>
        <p>
          The Service and its original content (excluding Content provided by
          users), features, and functionality are and will remain the exclusive
          property of our company and its licensors. The Service is protected by
          copyright, trademark, and other laws of both the United States and
          foreign countries. Our trademarks and trade dress may not be used in
          connection with any product or service without the prior written
          consent of our company.
        </p>
      </section>

      <section>
        <h2>Termination</h2>
        <p>
          We may terminate or suspend your account immediately, without prior
          notice or liability, for any reason whatsoever, including without
          limitation if you breach the Terms.
        </p>
        <p>
          Upon termination, your right to use the Service will immediately
          cease. If you wish to terminate your account, you may simply
          discontinue using the Service or contact us to request account
          deletion.
        </p>
      </section>

      <section>
        <h2>Limitation of Liability</h2>
        <p>
          In no event shall our company, nor its directors, employees, partners,
          agents, suppliers, or affiliates, be liable for any indirect,
          incidental, special, consequential or punitive damages, including
          without limitation, loss of profits, data, use, goodwill, or other
          intangible losses, resulting from:
        </p>
        <ul>
          <li>
            Your access to or use of or inability to access or use the Service;
          </li>
          <li>Any conduct or content of any third party on the Service;</li>
          <li>Any content obtained from the Service; and</li>
          <li>
            Unauthorized access, use, or alteration of your transmissions or
            content.
          </li>
        </ul>
      </section>

      <section>
        <h2>Disclaimer</h2>
        <p>
          Your use of the Service is at your sole risk. The Service is provided
          on an "AS IS" and "AS AVAILABLE" basis. The Service is provided
          without warranties of any kind, whether express or implied, including,
          but not limited to, implied warranties of merchantability, fitness for
          a particular purpose, non-infringement, or course of performance.
        </p>
      </section>

      <section>
        <h2>Governing Law</h2>
        <p>
          These Terms shall be governed and construed in accordance with the
          laws of the United States, without regard to its conflict of law
          provisions.
        </p>
        <p>
          Our failure to enforce any right or provision of these Terms will not
          be considered a waiver of those rights. If any provision of these
          Terms is held to be invalid or unenforceable by a court, the remaining
          provisions of these Terms will remain in effect.
        </p>
      </section>

      <section>
        <h2>Changes to Terms</h2>
        <p>
          We reserve the right, at our sole discretion, to modify or replace
          these Terms at any time. If a revision is material, we will try to
          provide at least 30 days' notice prior to any new terms taking effect.
          What constitutes a material change will be determined at our sole
          discretion.
        </p>
        <p>
          By continuing to access or use our Service after those revisions
          become effective, you agree to be bound by the revised terms. If you
          do not agree to the new terms, please stop using the Service.
        </p>
      </section>

      <section>
        <h2>Contact Us</h2>
        <p>If you have any questions about these Terms, please contact us:</p>
        <ul>
          <li>By email: terms@example.com</li>
          <li>By phone: +1 (555) 123-4567</li>
          <li>By mail: 123 Legal Street, Terms City, TC 12345</li>
        </ul>
      </section>
    </LegalPageLayout>
  );
};

export default TermsOfService;
